import React, { useState, useEffect, useRef } from 'react';
import { MapContainer, TileLayer, Polygon, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { FiSearch, FiX, FiUser, FiMapPin, FiFilter, FiInfo, FiCheckCircle, FiAlertCircle, FiLayers } from 'react-icons/fi';
import api from '../services/api';

// Fix Leaflet default icon
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

interface Feature {
  index: number;
  numero: string;
  coordinates: [number, number][];
  centroid: [number, number];
  superficie: number;
  parcelle_id: string | null;
  parcelle: any;
  attribuee: boolean;
  attribuable: boolean;
}

interface Projet {
  id: string;
  libelle: string;
  reference: string;
  localisation: string;
  has_gpx: boolean;
  nb_parcelles: number;
}

// Component to fit map to bounds
function FitBounds({ features }: { features: Feature[] }) {
  const map = useMap();
  useEffect(() => {
    if (features.length > 0) {
      const allCoords = features.flatMap(f => f.coordinates);
      if (allCoords.length > 0) {
        const bounds = L.latLngBounds(allCoords.map(c => [c[0], c[1]] as [number, number]));
        map.fitBounds(bounds, { padding: [30, 30] });
      }
    }
  }, [features, map]);
  return null;
}

const Cartographie = () => {
  const [projets, setProjets] = useState<Projet[]>([]);
  const [selectedProjet, setSelectedProjet] = useState('');
  const [features, setFeatures] = useState<Feature[]>([]);
  const [stats, setStats] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  // Filters
  const [filterAttrib, setFilterAttrib] = useState('all'); // all, attribuee, non_attribuee
  const [searchParcelle, setSearchParcelle] = useState('');
  const [highlightedId, setHighlightedId] = useState<string | null>(null);

  // Attribution modal
  const [showAttribModal, setShowAttribModal] = useState(false);
  const [selectedFeature, setSelectedFeature] = useState<Feature | null>(null);
  const [membreSearch, setMembreSearch] = useState('');
  const [membreResults, setMembreResults] = useState<any[]>([]);
  const [selectedMembre, setSelectedMembre] = useState<any>(null);
  const [membresLoading, setMembresLoading] = useState(false);
  const searchTimeout = useRef<any>(null);


  // Info panel
  const [showInfoPanel, setShowInfoPanel] = useState(false);
  const [infoParcelle, setInfoParcelle] = useState<Feature | null>(null);

  // Load projects
  useEffect(() => {
    api.get('/cartographie/projets').then(r => {
      setProjets(r.data);
      if (r.data.length > 0) {
        const withGpx = r.data.find((p: Projet) => p.has_gpx);
        if (withGpx) setSelectedProjet(withGpx.id);
        else setSelectedProjet(r.data[0].id);
      }
    }).catch(() => {});
  }, []);

  // Load parcelles when project changes
  useEffect(() => {
    if (!selectedProjet) return;
    setLoading(true);
    api.get(`/cartographie/parcelles/${selectedProjet}`).then(r => {
      setFeatures(r.data.features || []);
      setStats(r.data.stats);
      setLoading(false);
    }).catch(() => { setLoading(false); });
  }, [selectedProjet]);

  // Filter features
  const filteredFeatures = features.filter(f => {
    if (filterAttrib === 'attribuee' && !f.attribuee) return false;
    if (filterAttrib === 'non_attribuee' && (f.attribuee || !f.attribuable)) return false;
    if (filterAttrib === 'non_attribuable' && f.attribuable) return false;
    if (searchParcelle) {
      const q = searchParcelle.toLowerCase();
      const matchNum = f.numero.includes(q);
      const matchNom = f.parcelle?.attributaire_nom?.toLowerCase().includes(q);
      const matchNicad = f.parcelle?.nicad?.toLowerCase().includes(q);
      if (!matchNum && !matchNom && !matchNicad) return false;
    }
    return true;
  });

  // Polygon colors
  const getColor = (f: Feature) => {
    if (highlightedId && (f.parcelle_id === highlightedId || f.numero === highlightedId)) return '#ff6600';
    if (!f.attribuable) return '#95a5a6'; // Gris - non attribuable
    if (f.attribuee) return '#27ae60';
    if (f.parcelle_id) return '#3498db';
    return '#e74c3c';
  };

  const getStyle = (f: Feature) => ({
    color: getColor(f),
    weight: highlightedId && (f.parcelle_id === highlightedId || f.numero === highlightedId) ? 3 : 1.5,
    fillColor: getColor(f),
    fillOpacity: highlightedId && (f.parcelle_id === highlightedId || f.numero === highlightedId) ? 0.6 : 0.3,
  });

  // Toggle attribuable
  const handleToggleAttribuable = async (f: Feature) => {
    try {
      if (!f.parcelle_id) {
        // Create parcelle first
        const createRes = await api.post('/cartographie/create-parcelle', {
          numero: f.numero,
          superficie: f.superficie,
          projet_id: selectedProjet,
          attribuable: !f.attribuable
        });
      } else {
        await api.put(`/cartographie/toggle-attribuable/${f.parcelle_id}`, {
          attribuable: !f.attribuable
        });
      }
      setSuccess(!f.attribuable ? 'Parcelle marquée attribuable' : 'Parcelle marquée non attribuable');
      // Reload
      const res = await api.get(`/cartographie/parcelles/${selectedProjet}`);
      setFeatures(res.data.features || []);
      setStats(res.data.stats);
      // Update info panel if open
      if (infoParcelle && infoParcelle.numero === f.numero) {
        const updated = (res.data.features || []).find((ft: Feature) => ft.numero === f.numero);
        if (updated) setInfoParcelle(updated);
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Erreur');
    }
    setTimeout(() => { setSuccess(''); setError(''); }, 3000);
  };

  // Member search for attribution
  const handleMembreSearch = (val: string) => {
    setMembreSearch(val);
    if (selectedMembre) { setSelectedMembre(null); }
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    if (val.trim().length < 2) { setMembreResults([]); return; }

    searchTimeout.current = setTimeout(() => {
      setMembresLoading(true);
      api.get('/cartographie/search-membres', { params: { q: val.trim() } })
        .then(r => setMembreResults(Array.isArray(r.data) ? r.data : []))
        .catch(() => setMembreResults([]))
        .finally(() => setMembresLoading(false));
    }, 300);
  };

  // Attribution
  const openAttribModal = (f: Feature) => {
    setSelectedFeature(f);
    setShowAttribModal(true);
    setMembreSearch('');
    setMembreResults([]);
    setSelectedMembre(null);
    if (f.parcelle?.user_id && f.parcelle?.attributaire_nom) {
      setSelectedMembre({
        id: f.parcelle.user_id,
        prenomnom: f.parcelle.attributaire_nom,
        matricule: f.parcelle.attributaire_matricule
      });
      setMembreSearch(f.parcelle.attributaire_nom);
    }
  };

  const handleAttribution = async () => {
    if (!selectedFeature) return;

    try {
      // If no parcelle exists yet, create one first
      let parcelleId = selectedFeature.parcelle_id;
      if (!parcelleId) {
        const createRes = await api.post('/cartographie/create-parcelle', {
          numero: selectedFeature.numero,
          superficie: selectedFeature.superficie,
          projet_id: selectedProjet,
          user_id: selectedMembre?.id || null,
          description: `Parcelle ${selectedFeature.numero}`
        });
        parcelleId = createRes.data.id;
      } else {
        await api.put(`/cartographie/attribuer/${parcelleId}`, {
          user_id: selectedMembre?.id || null
        });
      }

      setSuccess(selectedMembre ? 'Parcelle attribuée avec succès' : 'Attribution retirée');
      setShowAttribModal(false);

      // Reload
      const res = await api.get(`/cartographie/parcelles/${selectedProjet}`);
      setFeatures(res.data.features || []);
      setStats(res.data.stats);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Erreur');
    }
    setTimeout(() => { setSuccess(''); setError(''); }, 3000);
  };

  const removeAttribution = async () => {
    if (!selectedFeature?.parcelle_id) return;
    try {
      await api.put(`/cartographie/attribuer/${selectedFeature.parcelle_id}`, { user_id: null });
      setSuccess('Attribution retirée');
      setShowAttribModal(false);
      const res = await api.get(`/cartographie/parcelles/${selectedProjet}`);
      setFeatures(res.data.features || []);
      setStats(res.data.stats);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Erreur');
    }
    setTimeout(() => { setSuccess(''); setError(''); }, 3000);
  };

  // Show info panel on polygon click
  const handlePolygonClick = (f: Feature) => {
    setInfoParcelle(f);
    setShowInfoPanel(true);
  };

  const currentProjet = projets.find(p => p.id === selectedProjet);

  return (
    <div className="cartographie-page">
      <h1 className="page-title"><FiMapPin /> Cartographie des parcelles</h1>

      {success && <div className="alert alert-success">{success}</div>}
      {error && <div className="alert alert-error">{error}</div>}

      {/* Toolbar */}
      <div className="carto-toolbar">
        <div className="carto-toolbar-left">
          <div className="form-group" style={{ marginBottom: 0, minWidth: 220 }}>
            <select className="form-input" value={selectedProjet} onChange={e => setSelectedProjet(e.target.value)}>
              <option value="">-- Sélectionner un projet --</option>
              {projets.map(p => (
                <option key={p.id} value={p.id}>{p.libelle} {p.has_gpx ? '(GPX)' : ''}</option>
              ))}
            </select>
          </div>

          <div className="carto-filter-group">
            <FiFilter />
            <select className="form-input form-input-sm" value={filterAttrib} onChange={e => setFilterAttrib(e.target.value)}>
              <option value="all">Toutes</option>
              <option value="attribuee">Attribuées</option>
              <option value="non_attribuee">Non attribuées</option>
              <option value="non_attribuable">Non attribuables</option>
            </select>
          </div>

          <div className="carto-search">
            <FiSearch className="carto-search-icon" />
            <input
              type="text"
              className="form-input form-input-sm"
              placeholder="N° parcelle, nom..."
              value={searchParcelle}
              onChange={e => setSearchParcelle(e.target.value)}
            />
            {searchParcelle && <button className="carto-search-clear" onClick={() => setSearchParcelle('')}><FiX /></button>}
          </div>
        </div>

        <div className="carto-toolbar-right" />
      </div>

      {/* Stats bar */}
      {stats && (
        <div className="carto-stats">
          <div className="carto-stat">
            <FiLayers />
            <span><strong>{stats.total_polygons}</strong> parcelles sur la carte</span>
          </div>
          <div className="carto-stat stat-green">
            <FiCheckCircle />
            <span><strong>{stats.attribuees}</strong> attribuées</span>
          </div>
          <div className="carto-stat stat-red">
            <FiAlertCircle />
            <span><strong>{stats.non_attribuees}</strong> non attribuées</span>
          </div>
          {stats.non_attribuables > 0 && (
            <div className="carto-stat stat-gray">
              <FiAlertCircle />
              <span><strong>{stats.non_attribuables}</strong> non attribuables</span>
            </div>
          )}
          <div className="carto-stat">
            <FiMapPin />
            <span><strong>{stats.total_parcelles}</strong> en base</span>
          </div>
        </div>
      )}

      {/* Map + Info panel */}
      <div className="carto-container">
        <div className={`carto-map-wrapper ${showInfoPanel ? 'with-panel' : ''}`}>
          {loading && <div className="carto-loading">Chargement de la carte...</div>}

          {!selectedProjet ? (
            <div className="carto-empty">
              <FiMapPin size={48} />
              <p>Sélectionnez un projet pour afficher la carte</p>
            </div>
          ) : features.length === 0 && !loading ? (
            <div className="carto-empty">
              <FiMapPin size={48} />
              <p>Aucun fichier GPX pour ce projet</p>
              <p style={{ fontSize: 13, color: '#aaa' }}>Chargez un fichier GPX depuis la page Projets</p>
            </div>
          ) : (
            <MapContainer
              center={[14.829, -17.195]}
              zoom={18}
              style={{ height: '100%', width: '100%', borderRadius: 8 }}
              scrollWheelZoom={true}
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/">OSM</a>'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              <TileLayer
                url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
                attribution="&copy; Esri"
              />

              <FitBounds features={filteredFeatures} />

              {filteredFeatures.map(f => (
                <Polygon
                  key={f.numero}
                  positions={f.coordinates}
                  pathOptions={getStyle(f)}
                  eventHandlers={{
                    click: () => handlePolygonClick(f),
                    mouseover: (e) => {
                      e.target.setStyle({ fillOpacity: 0.5, weight: 2.5 });
                      const status = !f.attribuable ? '<span style="color:#95a5a6">Non attribuable</span>' : f.attribuee ? f.parcelle?.attributaire_nom : '<span style="color:#e74c3c">Non attribuée</span>';
                      e.target.bindTooltip(
                        `<strong>Parcelle ${f.numero}</strong><br/>${status}`,
                        { sticky: true }
                      ).openTooltip();
                    },
                    mouseout: (e) => {
                      e.target.setStyle(getStyle(f));
                      e.target.unbindTooltip();
                    }
                  }}
                />
              ))}
            </MapContainer>
          )}
        </div>

        {/* Info Panel */}
        {showInfoPanel && infoParcelle && (
          <div className="carto-info-panel">
            <div className="carto-info-header">
              <h3>Parcelle {infoParcelle.numero}</h3>
              <button className="carto-info-close" onClick={() => setShowInfoPanel(false)}><FiX /></button>
            </div>

            <div className="carto-info-body">
              <div className="carto-info-row">
                <label>Superficie</label>
                <span>{infoParcelle.superficie ? `${infoParcelle.superficie.toLocaleString('fr-FR')} m²` : '-'}</span>
              </div>
              {infoParcelle.parcelle?.lot && (
                <div className="carto-info-row">
                  <label>Lot</label>
                  <span>{infoParcelle.parcelle.lot}</span>
                </div>
              )}
              {infoParcelle.parcelle?.nicad && (
                <div className="carto-info-row">
                  <label>NICAD</label>
                  <span>{infoParcelle.parcelle.nicad}</span>
                </div>
              )}
              {infoParcelle.parcelle?.numtf && (
                <div className="carto-info-row">
                  <label>N° TF</label>
                  <span>{infoParcelle.parcelle.numtf}</span>
                </div>
              )}

              <div className="carto-info-divider" />

              {!infoParcelle.attribuable ? (
                <div>
                  <div className="carto-info-badge badge-gray">
                    <FiAlertCircle /> Non attribuable
                  </div>
                  <p style={{ fontSize: 12, color: '#888', margin: '4px 0 0' }}>Espace vert, voirie, ou zone commune</p>
                </div>
              ) : infoParcelle.attribuee ? (
                <div className="carto-info-attributaire">
                  <div className="carto-info-badge badge-green">
                    <FiCheckCircle /> Attribuée
                  </div>
                  <div className="carto-info-member">
                    <FiUser />
                    <div>
                      <strong>{infoParcelle.parcelle?.attributaire_nom}</strong>
                      {infoParcelle.parcelle?.attributaire_matricule && <span>Mat: {infoParcelle.parcelle.attributaire_matricule}</span>}
                      {infoParcelle.parcelle?.attributaire_telephone && <span>{infoParcelle.parcelle.attributaire_telephone}</span>}
                      {infoParcelle.parcelle?.attributaire_email && <span>{infoParcelle.parcelle.attributaire_email}</span>}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="carto-info-badge badge-red">
                  <FiAlertCircle /> Non attribuée
                </div>
              )}

              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 16 }}>
                {infoParcelle.attribuable && (
                  <button className="btn btn-primary btn-block" onClick={() => openAttribModal(infoParcelle)}>
                    <FiUser /> {infoParcelle.attribuee ? 'Modifier l\'attribution' : 'Attribuer à un membre'}
                  </button>
                )}
                <button
                  className={`btn btn-block ${infoParcelle.attribuable ? 'btn-outline-gray' : 'btn-success'}`}
                  onClick={() => handleToggleAttribuable(infoParcelle)}
                >
                  {infoParcelle.attribuable ? 'Marquer non attribuable' : 'Marquer attribuable'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Legend */}
      <div className="carto-legend">
        <span className="carto-legend-item"><span className="carto-legend-color" style={{ background: '#27ae60' }} /> Attribuée</span>
        <span className="carto-legend-item"><span className="carto-legend-color" style={{ background: '#3498db' }} /> En base (non attribuée)</span>
        <span className="carto-legend-item"><span className="carto-legend-color" style={{ background: '#e74c3c' }} /> Sans enregistrement</span>
        <span className="carto-legend-item"><span className="carto-legend-color" style={{ background: '#95a5a6' }} /> Non attribuable</span>
        <span className="carto-legend-item"><span className="carto-legend-color" style={{ background: '#ff6600' }} /> Sélectionnée</span>
      </div>

      {/* Liste parcelles sous la carte */}
      <div className="card" style={{ marginTop: 16 }}>
        <div className="card-header">
          <span>Liste des parcelles ({filteredFeatures.length})</span>
        </div>
        <div className="card-body">
          <div className="table-responsive">
            <table className="data-table">
              <thead>
                <tr>
                  <th>N°</th>
                  <th>Superficie</th>
                  <th>Attributaire</th>
                  <th>Lot</th>
                  <th>NICAD</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredFeatures.slice(0, 100).map(f => (
                  <tr
                    key={f.numero}
                    className={highlightedId === f.numero ? 'row-highlight' : ''}
                    onMouseEnter={() => setHighlightedId(f.numero)}
                    onMouseLeave={() => setHighlightedId(null)}
                  >
                    <td><strong>{f.numero}</strong></td>
                    <td>{f.superficie ? `${f.superficie.toLocaleString('fr-FR')} m²` : '-'}</td>
                    <td>
                      {!f.attribuable ? (
                        <span className="badge badge-orange">Non attribuable</span>
                      ) : f.attribuee ? (
                        <span className="badge badge-green">{f.parcelle?.attributaire_nom}</span>
                      ) : (
                        <span className="badge badge-gray">Non attribuée</span>
                      )}
                    </td>
                    <td>{f.parcelle?.lot || '-'}</td>
                    <td>{f.parcelle?.nicad || '-'}</td>
                    <td>
                      <div className="actions">
                        <button className="btn-icon btn-info" title="Voir sur la carte" onClick={() => { handlePolygonClick(f); setHighlightedId(f.numero); }}>
                          <FiInfo />
                        </button>
                        {f.attribuable && (
                          <button className="btn-icon btn-primary" title="Attribuer" onClick={() => openAttribModal(f)}>
                            <FiUser />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
                {filteredFeatures.length > 100 && (
                  <tr><td colSpan={6} style={{ textAlign: 'center', color: '#999' }}>... et {filteredFeatures.length - 100} autres parcelles</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Attribution Modal */}
      {showAttribModal && selectedFeature && (
        <div className="modal-overlay" onClick={() => setShowAttribModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 500 }}>
            <div className="modal-header">
              <h3>Attribution - Parcelle {selectedFeature.numero}</h3>
              <button className="modal-close" onClick={() => setShowAttribModal(false)}><FiX /></button>
            </div>
            <div className="modal-body">
              {selectedFeature.attribuee && (
                <div className="carto-current-attrib">
                  <span>Attribuée actuellement à: <strong>{selectedFeature.parcelle?.attributaire_nom}</strong></span>
                  <button className="btn btn-danger btn-sm" onClick={removeAttribution}>
                    Retirer l'attribution
                  </button>
                </div>
              )}

              <div className="form-group">
                <label><FiUser style={{ verticalAlign: 'middle', marginRight: 4 }} /> Rechercher un membre</label>
                <div className="autocomplete-wrapper">
                  <div className="autocomplete-input-wrapper">
                    <FiSearch className="autocomplete-search-icon" />
                    <input
                      type="text"
                      className={`form-input autocomplete-input ${selectedMembre ? 'has-value' : ''}`}
                      placeholder="Nom, matricule, email..."
                      value={membreSearch}
                      onChange={e => handleMembreSearch(e.target.value)}
                      autoComplete="off"
                    />
                    {selectedMembre && (
                      <button type="button" className="autocomplete-clear" onClick={() => { setSelectedMembre(null); setMembreSearch(''); setMembreResults([]); }}>
                        <FiX />
                      </button>
                    )}
                  </div>

                  {selectedMembre && (
                    <div className="autocomplete-selected">
                      <FiUser />
                      <div>
                        <strong>{selectedMembre.prenomnom}</strong>
                        <span>
                          {selectedMembre.matricule && `Mat: ${selectedMembre.matricule}`}
                        </span>
                      </div>
                    </div>
                  )}

                  {!selectedMembre && membreResults.length > 0 && (
                    <div className="autocomplete-dropdown">
                      {membresLoading && <div className="autocomplete-item loading">Recherche...</div>}
                      {membreResults.map(m => (
                        <div key={m.id} className="autocomplete-item" onClick={() => { setSelectedMembre(m); setMembreSearch(m.prenomnom); setMembreResults([]); }}>
                          <div className="autocomplete-item-main">
                            <strong>{m.prenomnom}</strong>
                          </div>
                          <div className="autocomplete-item-sub">
                            {m.matricule && <span>Mat: {m.matricule}</span>}
                            {m.telephone && <span>{m.telephone}</span>}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {!selectedMembre && !membresLoading && membreSearch.length >= 2 && membreResults.length === 0 && (
                    <div className="autocomplete-dropdown">
                      <div className="autocomplete-item loading">Aucun membre trouvé</div>
                    </div>
                  )}
                </div>
              </div>

              <div className="carto-info-row" style={{ marginTop: 12 }}>
                <label>Superficie</label>
                <span>{selectedFeature.superficie ? `${selectedFeature.superficie.toLocaleString('fr-FR')} m²` : '-'}</span>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={() => setShowAttribModal(false)}>Annuler</button>
              <button className="btn btn-primary" onClick={handleAttribution} disabled={!selectedMembre}>
                <FiCheckCircle /> Attribuer
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default Cartographie;
